import { useQueries, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { FINANCIAL_FIELDS, FINANCIAL_READ_BATCH_SIZE, unknownFinancials, expireFinancialEvidence, retainFinancialEvidence, type MemberFinancials, type FinancialSettings, type FinancialThresholds } from "@shared/member-financials";

export const observationsKey = "/api/app/member-financials";
export const settingsKey = `${observationsKey}/settings`;

export function financialIdBatches(ids: number[]): number[][] {
  const normalized = [...new Set(ids.filter(id => Number.isSafeInteger(id) && id > 0))].sort((a, b) => a - b);
  const batches: number[][] = [];
  for (let i = 0; i < normalized.length; i += FINANCIAL_READ_BATCH_SIZE) batches.push(normalized.slice(i, i + FINANCIAL_READ_BATCH_SIZE));
  return batches;
}

let readsInFlight = 0;
const waitingReads: Array<() => void> = [];
async function boundedRead<T>(read: () => Promise<T>): Promise<T> {
  if (readsInFlight >= 2) await new Promise<void>(resolve => waitingReads.push(resolve));
  else readsInFlight++;
  try { return await read(); }
  finally {
    const next = waitingReads.shift();
    if (next) next();
    else readsInFlight--;
  }
}

/** Cache first; bounded small live batches render independently as they complete. */
export function useMemberFinancials(ids: number[]) {
  const [, tick] = useState(0);
  // Re-evaluate expiry even when the tab remains mounted without a network read.
  useEffect(() => {
    const timer = setInterval(() => tick(value => value + 1), 1000);
    const onFocus = () => tick(value => value + 1);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(timer); window.removeEventListener("focus", onFocus); };
  }, []);
  const batches = financialIdBatches(ids);
  const cacheQueries = useQueries({
    queries: batches.map(batch => ({
      queryKey: [observationsKey, "cache", batch.join(",")],
      queryFn: async (): Promise<{ members: MemberFinancials[] }> =>
        (await apiRequest("GET", `${observationsKey}?ids=${batch.join(",")}&cache=only`)).json(),
      staleTime: 30_000,
      retry: false,
      refetchOnWindowFocus: false,
    })),
  });
  const queries = useQueries({
    queries: batches.map((batch, index) => ({
      queryKey: [observationsKey, batch.join(",")],
      enabled: !cacheQueries[index].isPending,
      queryFn: async (): Promise<{ members: MemberFinancials[] }> => boundedRead(async () => {
        const result = await (await apiRequest("GET", `${observationsKey}?ids=${batch.join(",")}`)).json();
        if (!Array.isArray(result?.members)) throw new Error("Financial evidence returned an invalid response.");
        return result;
      }),
      staleTime: 30_000,
      retry: false,
      refetchOnWindowFocus: false,
    })),
  });
  const members = new Map<number, MemberFinancials>();
  let hasPendingWithoutCache = false;
  batches.forEach((batch, index) => {
    const query = queries[index];
    const reason = query.isError
      ? parseErrorMessage(query.error, "Source observations could not be loaded.")
      : query.isPending ? "Source observations are loading." : "Not yet harvested.";
    batch.forEach(id => members.set(id, unknownFinancials(id, reason)));
    // Permission failures must never fall back to cached values.
    const denied = [query, cacheQueries[index]].some(q => q.isError && /^(401|403|404)\b/.test(String(q.error instanceof Error ? q.error.message : q.error)));
    if (denied) return;
    const cached = cacheQueries[index].data?.members;
    const candidates = [
      ...(Array.isArray(cached) ? cached : []),
      ...(query.data?.members ?? []),
    ];
    candidates.forEach(member => {
      if (!member || !batch.includes(member.memberId)) return;
      const valid = FINANCIAL_FIELDS.every(field => {
        const datum = member.fields?.[field];
        return datum && ["unknown", "unverified", "unavailable", "invalid", "stale", "observed"].includes(datum.state)
          && typeof datum.display === "string" && typeof datum.reason === "string"
          && (datum.observedAt === null || typeof datum.observedAt === "string");
      });
      const hasValue = valid && FINANCIAL_FIELDS.some(field => ["observed", "stale"].includes(member.fields[field].state));
      // A cache miss is not a completed live read; retain its loading placeholder.
      if (query.isPending && !hasValue) return;
      members.set(member.memberId, valid && member.automation?.enabled === false
        ? expireFinancialEvidence(query.isError ? retainFinancialEvidence(member, "Refresh failed.") : member)
        : unknownFinancials(member.memberId, "Financial evidence returned an invalid response."));
    });
    batch.forEach(id => {
      const member = members.get(id)!;
      if (query.isPending && member.fields.balance.reason === "Source observations are loading.") hasPendingWithoutCache = true;
      if (query.isError && member.fields.balance.reason === reason) {
        members.set(id, retainFinancialEvidence(member, "Financial observation refresh failed."));
      }
    });
  });
  return {
    members,
    isPending: hasPendingWithoutCache,
    isFetching: queries.some(query => query.isFetching),
    error: queries.find(query => query.isError)?.error,
    reload: () => Promise.all(queries.map(query => query.refetch())),
  };
}

export function useFinancialSettings(propertyId: number, enabled: boolean) {
  const client = useQueryClient();
  const url = `${settingsKey}?propertyId=${propertyId}`;
  const query = useQuery<FinancialSettings>({
    queryKey: [settingsKey, propertyId],
    queryFn: async () => (await apiRequest("GET", url)).json(),
    enabled,
    refetchOnWindowFocus: false,
  });
  const mutation = useMutation({
    mutationFn: async (thresholds: FinancialThresholds | null) => {
      if (!query.data?.canEdit || query.isError) throw new Error("Financial settings are read-only.");
      if (thresholds === null && propertyId <= 0) throw new Error("Only property overrides can be restored.");
      await apiRequest(thresholds === null ? "DELETE" : "PUT", url, thresholds ?? undefined);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: [settingsKey] });
      await client.invalidateQueries({ queryKey: [observationsKey] });
    },
  });
  return { query, mutation };
}
